/**
 * UserPreferencesContext.tsx
 *
 * React context for storing and persisting user preferences such as:
 * - language (i18n locale)
 * - region (regional formatting / content preferences)
 * - measurement system (Metric/Imperial)
 * - timezone (IANA timezone string)
 *
 * Data flow
 * 1) Provider bootstraps with `defaultPreferences`.
 * 2) `loadUserSettings()` fetches the current user's settings from the backend and
 *    merges them into local state.
 * 3) `updatePreferences()` merges partial updates into local state and persists
 *    the full updated settings to the backend.
 *
 * Side effects
 * - When `preferences.language` changes, we call `i18n.changeLanguage()` so the UI
 *   switches language immediately.
 * - API calls are authenticated with a Keycloak access token (auto-refreshed as needed).
 *
 * Quality notes (SEP rubric alignment)
 * - Centralizes preference management to improve modifiability and reduce duplication.
 * - Documents backend field mapping to prevent "magic property name" drift.
 * - Uses typed interfaces for clarity and analyzability.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { isAxiosError } from 'axios';
import client from '../api/client';
import {
  defaultPreferences,
  preferencesFromApi,
  settingsPayload,
  type UserPreferences,
} from './userPreferences';

/**
 * Context API exposed to components.
 */
interface UserPreferencesContextType {
  /** Current in-memory preferences. */
  preferences: UserPreferences;

  /**
   * Merge in partial preference changes and persist them to the backend.
   *
   * @param newPreferences - only the fields that changed
   * @returns Promise that resolves once local state has been updated.
   *
   * IMPORTANT:
   * This function performs optimistic UI updates (state is updated immediately),
   * then attempts to persist changes. On network/auth failure, `error` is set.
   */
  updatePreferences: (newPreferences: Partial<UserPreferences>) => Promise<void>;

  /**
   * Fetch preferences from backend and merge into the local defaults.
   * Intended to be called on app start or after login.
   */
  loadUserSettings: () => Promise<void>;

  /** True while reading/saving preferences. */
  isLoading: boolean;

  /** Error message from the last failed load/save attempt (if any). */
  error: string | null;
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined);

interface UserPreferencesProviderProps {
  children: ReactNode;
}

/**
 * UserPreferencesProvider
 *
 * Wrap your app with this provider to make preferences accessible via `useUserPreferences()`.
 */
export const UserPreferencesProvider: React.FC<UserPreferencesProviderProps> = ({ children }) => {
  const { i18n } = useTranslation();

  /**
   * Start with defaults in the language i18next started in, the device's;
   * preferences are loaded from the backend via `loadUserSettings()`, and the
   * account's language replaces the device's only when it has one.
   */
  const [preferences, setPreferences] = useState<UserPreferences>(() => ({
    ...defaultPreferences,
    language: i18n.language || defaultPreferences.language,
  }));
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The newest preferences, readable without re-creating the callbacks below.
  const latest = useRef(preferences);
  useEffect(() => {
    latest.current = preferences;
  }, [preferences]);

  // The tail of the write queue, so preference saves land in the order they
  // were made rather than the order the network happens to finish them.
  const inFlight = useRef<Promise<void>>(Promise.resolve());

  // How many loads and saves are outstanding. A count rather than a flag per
  // operation: with saves queued, the first one to finish would otherwise
  // clear `isLoading` while the rest were still waiting their turn, and a load
  // running beside a save would do the same. Only the last one out turns it
  // off, failures included -- so the count is decremented in `finally`, never
  // on the success path.
  const pending = useRef(0);
  const beginWork = useCallback(() => {
    pending.current += 1;
    setIsLoading(true);
  }, []);
  const endWork = useCallback(() => {
    pending.current = Math.max(0, pending.current - 1);
    if (pending.current === 0) setIsLoading(false);
  }, []);

  /**
   * Keep i18next language in sync with the stored preference.
   * This makes language switching immediate and global across the app.
   */
  useEffect(() => {
    if (i18n.language !== preferences.language) {
      i18n.changeLanguage(preferences.language);
    }
  }, [preferences.language, i18n]);

  /**
   * updatePreferences
   *
   * Merges the partial change into state, then writes the full set back.
   *
   * The write is here and not inside `setPreferences`. It used to be: the
   * request was fired from an async IIFE within the state updater, and React
   * calls an updater twice in StrictMode -- so every change sent two
   * simultaneous `PUT /users/me` for the same row. One took the row lock and
   * the other waited on it, and because that handler was `async def` doing
   * blocking work, the wait was on the event loop: the first request could
   * never be read to completion, and the whole API stopped answering with
   * nothing in its log. A state updater has to be free of side effects for
   * exactly this reason.
   *
   * `latest` mirrors the state so the merge still reads the newest value
   * without making this callback depend on it -- what the functional updater
   * was there for -- and is written before the state so two calls in one tick
   * compose rather than the second overwriting the first.
   *
   * It also awaits the request now, so a caller can wait for the save.
   */
  const updatePreferences = useCallback(async (newPreferences: Partial<UserPreferences>) => {
    const updated = { ...latest.current, ...newPreferences };
    latest.current = updated;
    setPreferences(updated);

    beginWork();
    setError(null);

    // Queued behind whatever is still in flight. `PUT /users/me` carries the
    // whole preference set, so two overlapping writes are two snapshots of the
    // same four fields with no version between them -- and the callers do not
    // await (`LanguagePicker.handleLanguageChange`), so two quick changes can
    // reach the server in either order. Whichever commits last wins, and a
    // reload can bring back the value the reader replaced. One at a time makes
    // the order the order they were made in.
    const send = inFlight.current
      .catch(() => undefined)
      .then(() => client.put('/users/me', settingsPayload(updated)));
    inFlight.current = send.then(
      () => undefined,
      () => undefined,
    );

    try {
      await send;
    } catch (err) {
      // Standardize error string for UI; keep full error in console for debugging.
      const errorMessage = err instanceof Error ? err.message : 'Failed to save user settings';
      setError(errorMessage);
      console.error('Failed to save user settings:', err);
      throw err;
    } finally {
      endWork();
    }
  }, [beginWork, endWork]);

  /**
   * loadUserSettings
   *
   * Fetches current user settings from the backend and merges them into local state.
   * If the user does not exist (404), we keep defaults without showing an error.
   */
  const loadUserSettings = useCallback(async () => {
    beginWork();
    setError(null);

    try {
      const response = await client.get('/users/me');

      // Merge onto existing preferences so defaults remain for any missing fields.
      const merged = {
        ...latest.current,
        ...preferencesFromApi(response.data),
      };
      latest.current = merged;
      setPreferences(merged);
    } catch (err) {
      if (isAxiosError(err) && err.response?.status === 404) {
        // No user settings found: keep defaults and avoid alarming the user.
        console.log('No user settings found, using defaults');
      } else {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load user settings';
        setError(errorMessage);
        console.error('Failed to load user settings:', err);
      }
    } finally {
      endWork();
    }
  }, [beginWork, endWork]);

  /**
   * Memoize context value to prevent unnecessary rerenders in consumers
   * when only unrelated provider state changes.
   */
  const value = useMemo<UserPreferencesContextType>(
    () => ({
      preferences,
      updatePreferences,
      loadUserSettings,
      isLoading,
      error,
    }),
    [preferences, updatePreferences, loadUserSettings, isLoading, error],
  );

  return <UserPreferencesContext.Provider value={value}>{children}</UserPreferencesContext.Provider>;
};

/**
 * useUserPreferences
 *
 * Hook to access user preference state and actions.
 * Throws if used outside of the provider to catch misconfiguration early.
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useUserPreferences = (): UserPreferencesContextType => {
  const context = useContext(UserPreferencesContext);
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider');
  }
  return context;
};
